import { useState } from "react";
import { CalendarDays, GraduationCap, UserCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "obc", label: "OBC" },
  { value: "sc", label: "SC" },
  { value: "st", label: "ST" },
  { value: "ews", label: "EWS" },
];

const QUALS = [
  { value: "10", label: "10th pass" },
  { value: "12", label: "12th pass" },
  { value: "grad", label: "Graduate" },
  { value: "bed", label: "B.Ed" },
  { value: "btech", label: "B.Tech / Engineering" },
];

// Eligibility map (qualification → list of sectors with base age limit)
const ELIGIBLE = {
  "10": [
    { sector: "SSC GD", base_min: 18, base_max: 23 },
    { sector: "Railway Group D", base_min: 18, base_max: 33 },
    { sector: "Defense (Soldier GD)", base_min: 17.5, base_max: 21 },
  ],
  "12": [
    { sector: "SSC CHSL", base_min: 18, base_max: 27 },
    { sector: "SSC MTS", base_min: 18, base_max: 25 },
    { sector: "SSC CPO", base_min: 20, base_max: 25 },
    { sector: "IBPS RRB Office Asst", base_min: 18, base_max: 28 },
    { sector: "Railway NTPC (UG)", base_min: 18, base_max: 30 },
    { sector: "NDA", base_min: 16.5, base_max: 19.5 },
  ],
  grad: [
    { sector: "SSC CGL", base_min: 18, base_max: 32 },
    { sector: "IBPS PO / Clerk", base_min: 20, base_max: 30 },
    { sector: "SBI PO", base_min: 21, base_max: 30 },
    { sector: "UPSC CSE", base_min: 21, base_max: 32 },
    { sector: "Railway NTPC (Grad)", base_min: 18, base_max: 33 },
    { sector: "State PSCs", base_min: 21, base_max: 35 },
    { sector: "Teaching (KVS PRT)", base_min: 18, base_max: 30 },
  ],
  bed: [
    { sector: "KVS TGT", base_min: 18, base_max: 35 },
    { sector: "NVS TGT", base_min: 18, base_max: 40 },
    { sector: "DSSSB Teacher", base_min: 20, base_max: 30 },
    { sector: "State Teacher Exams", base_min: 21, base_max: 40 },
  ],
  btech: [
    { sector: "PSU GATE (NTPC, IOCL, BHEL)", base_min: 21, base_max: 30 },
    { sector: "Railway JE", base_min: 18, base_max: 36 },
    { sector: "SSC JE", base_min: 18, base_max: 32 },
    { sector: "ISRO Scientist/Engineer", base_min: 21, base_max: 28 },
  ],
};

const RELAX = { general: 0, ews: 0, obc: 3, sc: 5, st: 5 };

function calcAgeYears(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

export default function EligibilityCalculator() {
  const [dob, setDob] = useState("");
  const [cat, setCat] = useState("general");
  const [qual, setQual] = useState("grad");
  const [submitted, setSubmitted] = useState(false);

  const age = calcAgeYears(dob);
  const relaxYears = RELAX[cat] || 0;
  const results = (ELIGIBLE[qual] || []).map((e) => {
    const effectiveMin = e.base_min;
    const effectiveMax = e.base_max + relaxYears;
    const ok = age != null && age >= effectiveMin && age <= effectiveMax;
    return { ...e, effectiveMin, effectiveMax, ok };
  });

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5 md:p-6"
      aria-labelledby="eligibility-h2"
      data-testid="eligibility-calculator"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
          <ShieldCheck className="w-4 h-4" aria-hidden="true" />
        </span>
        <h2
          id="eligibility-h2"
          className="text-lg md:text-xl font-extrabold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
        >
          Eligibility & Age Calculator
        </h2>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        Enter your details to see which exams you qualify for — based on your category&apos;s age relaxation.
      </p>

      <div className="grid gap-3">
        <label className="block">
          <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" /> Date of Birth
          </span>
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().split("T")[0]}
            data-testid="elig-dob"
            className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </label>

        <label className="block">
          <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
            <UserCircle2 className="w-3.5 h-3.5" /> Category
          </span>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="elig-category"
            className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Highest Qualification
          </span>
          <select
            value={qual}
            onChange={(e) => setQual(e.target.value)}
            data-testid="elig-qualification"
            className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            {QUALS.map((q) => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </label>

        <Button
          onClick={() => setSubmitted(true)}
          disabled={!dob}
          data-testid="elig-submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white min-h-11"
        >
          Check Eligibility
        </Button>
      </div>

      {submitted && (
        <div className="mt-5" data-testid="elig-results">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
            Your age: <span className="text-slate-900">{age != null ? `${age} years` : "—"}</span>
            {relaxYears > 0 && <span className="text-orange-600 font-semibold"> · +{relaxYears} yrs relaxation</span>}
          </div>
          <ul className="space-y-2">
            {results.map((r) => (
              <li
                key={r.sector}
                className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border ${
                  r.ok
                    ? "bg-green-50 border-green-200 text-green-900"
                    : "bg-slate-50 border-slate-200 text-slate-600"
                }`}
              >
                <span className="text-sm font-semibold">{r.sector}</span>
                <span
                  className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${
                    r.ok ? "bg-green-600 text-white" : "bg-slate-300 text-slate-700"
                  }`}
                  data-testid={`elig-badge-${r.sector.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  {r.ok ? "Eligible" : `Age ${r.effectiveMin}–${r.effectiveMax}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Age limits include {cat.toUpperCase()} category relaxation. Always cross-check the official notification for the exact cut-off date.
          </p>
        </div>
      )}
    </section>
  );
}
